import { useState, useMemo, useCallback } from 'react';

export const QUICK_RANGES = [
  {
    key: 'last-5-minutes',
    label: 'Last 5 minutes',
    value: {
      start: new Date(Date.now() - 5 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-15-minutes',
    label: 'Last 15 minutes',
    value: {
      start: new Date(Date.now() - 15 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-30-minutes',
    label: 'Last 30 minutes',
    value: {
      start: new Date(Date.now() - 30 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-1-hour',
    label: 'Last 1 hour',
    value: {
      start: new Date(Date.now() - 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-3-hours',
    label: 'Last 3 hours',
    value: {
      start: new Date(Date.now() - 3 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-6-hours',
    label: 'Last 6 hours',
    value: {
      start: new Date(Date.now() - 6 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-12-hours',
    label: 'Last 12 hours',
    value: {
      start: new Date(Date.now() - 12 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-24-hours',
    label: 'Last 24 hours',
    value: {
      start: new Date(Date.now() - 24 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'last-2-days',
    label: 'Last 2 days',
    value: {
      start: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    value: {
      start: new Date(Date.now() - 24 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'day-before-yesterday',
    label: 'Day before yesterday',
    value: {
      start: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
  {
    key: 'this-day-last-week',
    label: 'This day last week',
    value: {
      start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      end: new Date(Date.now()),
    },
  },
];

const getQuickRange = (quickRange: string) => {
  const range = QUICK_RANGES.find((range) => range.key === quickRange);
  if (!range) {
    throw new Error(`Quick range ${quickRange} not found`);
  }
  return {
    start: new Date(Date.now() - (new Date(Date.now()).getTime() - range.value.start.getTime())),
    end: new Date(Date.now()),
  };
};

interface UseTimeSelectorProps {
  setStartDate: (start: Date) => void;
  setEndDate: (end: Date) => void;
  start: Date | undefined;
  end: Date | undefined;
  loading: boolean;
  selectedQuickRange: string | undefined;
  setSelectedQuickRange: (quickRange: string) => void;
}

export const useTimeSelector = ({
  setStartDate,
  setEndDate,
  start,
  end,
  loading,
  selectedQuickRange,
  setSelectedQuickRange,
}: UseTimeSelectorProps) => {
  const [newStart, setNewStart] = useState<Date | undefined>(start);
  const [newEnd, setNewEnd] = useState<Date | undefined>(end);
  const [quickRange, setQuickRange] = useState<string | undefined>(selectedQuickRange);
  const [wasManuallyChanged, setWasManuallyChanged] = useState(false);

  const updateDisabled = useMemo(
    () => !newStart || !newEnd || (newStart === start && newEnd === end) || loading,
    [newStart, newEnd, start, end, loading]
  );

  const quickRangeHandler = (quickRange: (typeof QUICK_RANGES)[number]['key']) => {
    const { start, end } = getQuickRange(quickRange);
    setNewStart(start);
    setNewEnd(end);
    setQuickRange(quickRange);
    setSelectedQuickRange(quickRange);
    setWasManuallyChanged(false);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setNewStart(date);
    setWasManuallyChanged(true);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setNewEnd(date);
    setWasManuallyChanged(true);
  };

  const applyTimeRange = () => {
    if (newStart) {
      setStartDate(newStart);
    }
    if (newEnd) {
      setEndDate(newEnd);
    }

    // Clear quickRange if dates were manually changed
    if (wasManuallyChanged) {
      setQuickRange(undefined);
      setSelectedQuickRange('');
      setWasManuallyChanged(false);
    }
  };

  const refreshTimeRange = useCallback(() => {
    if (selectedQuickRange) {
      const { start: newStart, end: newEnd } = getQuickRange(selectedQuickRange);
      setStartDate(newStart);
      setEndDate(newEnd);
      setNewStart(newStart);
      setNewEnd(newEnd);
      return true;
    }
    return false;
  }, [selectedQuickRange, setStartDate, setEndDate, setNewStart, setNewEnd]);

  return {
    newStart,
    setNewStart: handleStartDateChange,
    newEnd,
    setNewEnd: handleEndDateChange,
    quickRange,
    setQuickRange,
    updateDisabled,
    quickRangeHandler,
    applyTimeRange,
    refreshTimeRange,
    QUICK_RANGES,
  };
};

export default useTimeSelector;
